function setupProductSliderNavButtons(emblaApi, navButtons) {
  const prevBtn = navButtons.querySelector(".embla__prev-button");
  const nextBtn = navButtons.querySelector(".embla__next-button");

  let touchStartTime = 0;
  const togglePrevNextBtnsState = () => {
    const activeSlide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()];
    navButtons.style.zIndex =
      activeSlide && activeSlide.dataset.mediaType === "video" ? 0 : 1;
  };

  emblaApi
    .on("select", togglePrevNextBtnsState)
    .on("init", togglePrevNextBtnsState)
    .on("reInit", togglePrevNextBtnsState)
    .on("pointerDown", () => {
      touchStartTime = Date.now();
    });

  const isClick = (e) => e.detail === 0 || Date.now() - touchStartTime < 250;
  const scrollPrev = (e) => {
    if (isClick(e)) {
      emblaApi.scrollPrev();
    }
  };
  const scrollNext = (e) => {
    if (isClick(e)) {
      emblaApi.scrollNext();
    }
  };
  prevBtn.addEventListener("click", scrollPrev, false);
  nextBtn.addEventListener("click", scrollNext, false);

  return () => {
    prevBtn.removeEventListener("click", scrollPrev, false);
    nextBtn.removeEventListener("click", scrollNext, false);
  };
}

function setupProductSliderIndicators(dots, embla) {
  const scrollSnaps = embla.scrollSnapList();
  const dotsFrag = document.createDocumentFragment();
  const dotsArray = scrollSnaps.map(() => document.createElement("div"));
  dots.innerHTML = "";
  dotsArray.forEach((dotNode) => {
    dotNode.classList.add("embla__dot");
    dotsFrag.appendChild(dotNode);
  });
  dots.appendChild(dotsFrag);

  const setSelectedDotBtn = () => {
    const previous = embla.previousScrollSnap();
    const selected = embla.selectedScrollSnap();

    if (dotsArray[previous]) {
      dotsArray[previous].classList.remove("is-selected");
    }
    if (dotsArray[selected]) {
      dotsArray[selected].classList.add("is-selected");
    }
  };

  embla.on("select", setSelectedDotBtn).on("init", setSelectedDotBtn);

  setSelectedDotBtn();

  return () => {
    dots.innerHTML = "";
    embla.off("select", setSelectedDotBtn).off("init", setSelectedDotBtn);
  };
}

class ProductMediaSlider extends HTMLElement {
  constructor() {
    super();

    this.watchResize = this.watchResize.bind(this);

    this.viewport = this.querySelector(".embla__viewport");
    this.autoplay = EmblaCarouselAutoplay();
    this.slider = EmblaCarousel(
      this.viewport,
      {
        loop: true,
        align: "start",
        watchResize: this.watchResize,
        breakpoints: {
          "(min-width:640px)": {
            inViewThreshold: 1,
          },
        },
      },
      [EmblaCarouselClassNames(), this.autoplay]
    );

    this.variantMediaContainer = this.querySelector(".js-variant-media");

    this.thumbnails = Array.from(this.querySelectorAll(".embla__thumbnail"));
    this.thumbnailHandlers = this.thumbnails.map(
      (thumb) => () => this.scrollToMedia(thumb.dataset.mediaId)
    );

    this.prevButton = this.querySelector(".embla__prev-button");
    this.nextButton = this.querySelector(".embla__next-button");

    this.navButtons = this.querySelector(".embla__nav-buttons");

    this.unsubscribeNavButtons = setupProductSliderNavButtons(
      this.slider,
      this.navButtons
    );

    this.dots = this.querySelector(".embla__dots");
    this.destroyDotBtns = setupProductSliderIndicators(this.dots, this.slider);

    this.toggleAutoplay = this.toggleAutoplay.bind(this);
    this.isDesktopLayout = window.matchMedia("(min-width:640px)");
    this.isDesktopLayout.addEventListener("change", this.toggleAutoplay);

    this.handleSelect = this.handleSelect.bind(this);
    this.toggleEmblaReady = this.toggleEmblaReady.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.initSizes = this.initSizes.bind(this);

    this.slider
      .on("init", this.initSizes)
      .on("reInit", this.initSizes)
      .on("init", this.toggleEmblaReady)
      .on("reInit", () => {
        this.destroyDotBtns();
        this.destroyDotBtns = setupProductSliderIndicators(
          this.dots,
          this.slider
        );
      })

      .on("reInit", this.toggleEmblaReady)

      .on("init", this.handleSelect)
      .on("select", this.handleSelect)

      .on("reInit", this.handleSelect)
      .on("select", () => this.autoplay.reset())
      .on("init", this.toggleAutoplay)
      .on("resize", this.handleEmblaResize.bind(this))
      .on("init", this.handleResize);

    this.thumbnails.forEach((slideNode, index) => {
      slideNode.addEventListener("click", this.thumbnailHandlers[index], false);
    });

    this.slider.on("destroy", () => {
      this.thumbnails.forEach((slideNode, index) => {
        slideNode.removeEventListener(
          "click",
          this.thumbnailHandlers[index],
          false
        );
      });
      this.unsubscribeNavButtons();
    });

    this.handleWindowResizeDebounced = debounce(
      this.handleWindowResize.bind(this),
      150
    );
    window.addEventListener("resize", this.handleWindowResizeDebounced);
  }

  connectedCallback() {
    const productModal = document.getElementById(this.dataset.modalId);
    Array.from(this.querySelectorAll(".product-modal-opener")).forEach(
      (button) => {
        button.addEventListener("click", (e) => {
          productModal.show(button.dataset.mediaId);
        });
      }
    );
  }

  disconnectedCallback() {
    if (this.slider) {
      this.slider.destroy();
    }
    window.removeEventListener("resize", this.handleWindowResizeDebounced);
  }

  setActiveMedia(activeId) {
    const mediaId = activeId.toString();
    let sliderChanged = false;
    const newMedia = this.variantMediaContainer.querySelector(
      `[data-media-id="${mediaId}"]`
    );
    const slideContainer = this.querySelector(".embla__container");
    Array.from(
      slideContainer.querySelectorAll(".product-media-slider__slide--variant")
    ).forEach((item) => {
      if (item.dataset.mediaId === mediaId) {
        return;
      }
      sliderChanged = true;
      this.variantMediaContainer.appendChild(item);
    });
    const activeThumb = this.querySelector(
      `.thumbnail-list__item--variant[data-media-id="${mediaId}"]`
    );
    if (activeThumb) {
      activeThumb.parentElement.prepend(activeThumb);
    }
    if (newMedia) {
      slideContainer.prepend(newMedia);
      sliderChanged = true;
    }

    if (sliderChanged) {
      this.toggleEmblaReady(this.slider, "resize");
      this.scrollToMedia(mediaId);
    }
  }

  scrollToMedia(mediaId) {
    this.slider.slideNodes().find((slide, index) => {
      if (slide.dataset.mediaId === mediaId) {
        this.slider.scrollTo(index);
        return true;
      }
    });
  }

  toggleAutoplay() {
    this.autoplay.setEnabled(this.isDesktopLayout.matches);
  }

  handleSelect() {
    const slidesInView = this.slider.slidesInView(true);
    this.slider.slideNodes().forEach((slide, i) => {
      const video = slide.querySelector("slider-video");
      if (!video) {
        return;
      }
      const inView = slidesInView.includes(i);
      if (inView) {
        video.play();
      } else {
        video.stop();
      }
    });
  }

  toggleEmblaReady(_embla, event) {
    const isResizeEvent = event === "resize";
    const toggleClass = isResizeEvent ? "remove" : "add";
    this.viewport.classList[toggleClass]("embla--is-ready");

    if (isResizeEvent) this.slider.reInit();
  }

  readSize(el) {
    return el.getBoundingClientRect().width.toFixed(2);
  }

  initSizes(embla) {
    this.containerSize = this.readSize(embla.containerNode());
    this.slideSizes = embla.slideNodes().map(this.readSize);
  }

  watchResize(embla, entries) {
    if (!this.slideSizes) {
      this.initSizes(embla);
      return;
    }
    const container = embla.containerNode();
    const slides = embla.slideNodes();
    for (const entry of entries) {
      const isContainer = entry.target === container;
      const slideIndex = slides.indexOf(entry.target);
      const lastSize = isContainer
        ? this.containerSize
        : this.slideSizes[slideIndex];
      const newSize = this.readSize(
        isContainer ? container : slides[slideIndex]
      );
      if (lastSize !== newSize) {
        requestAnimationFrame(() => {
          embla.reInit();
          embla.emit("resize");
        });
        break;
      }
    }
    return false;
  }

  handleEmblaResize(slider, event) {
    this.windowWidth = window.innerWidth;
    this.toggleEmblaReady(slider || this.slider, event || "resize");
  }

  handleWindowResize() {
    if (this.windowWidth !== window.innerWidth) {
      this.handleEmblaResize();
    }
    this.handleResize();
  }

  handleResize() {
    const isMobileLayout = window.innerWidth < 640;

    if (isMobileLayout === this.isMobileLayout) {
      return;
    }
    this.isMobileLayout = isMobileLayout;
    if (isMobileLayout) {
      this.slider.internalEngine().translate.toggleActive(true);
    } else {
      this.slider.internalEngine().translate.toggleActive(false);
      this.slider.internalEngine().translate.clear();
    }
  }
}
customElements.define("product-media-slider", ProductMediaSlider);
